'use client';
import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';
import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { NavVertical } from './nav-vertical';
import { NavHorizontal } from './nav-horizontal';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { getNavBackgroundColor, useNavColorVars } from './styles';
import { useBoolean } from '../hooks/use-boolean';
import { useSettingsContext } from '../settings/context';
import { navData } from '@/app/components/navData';
import { filterNavByPermission } from '@/lib/filterNavByPermission';
import { flattenNavItems } from '@/lib/flattenNav';
import { useRBACOptional } from '@/contexts/RBACContext';
import { NavSectionProps } from '../nav-section';
import { MenuButton } from './components/menu-button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import NextLink from 'next/link';
import { Searchbar } from './components/searchbar';
import { NotificationsDrawer } from './components/notifications-drawer';
import { SettingsButton } from './components/settings-button';
import { SubAccountSelector } from './components/sub-account-selector';
import { AccountDrawer } from './components/account-drawer';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { isAdminPanelFullscreenPath, shouldHideAppSidebar } from '@/lib/adminRoutes';
import { useMemo, useState, useEffect, useCallback, useRef, useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import { Iconify } from '@/components/common/iconify';
import Image from 'next/image';
import { customRouteTexts } from '@/lib/route-text-config';
import { useHeaderContext } from './header-context';
import { pathForNextNavigation, selfUrlToHomeHref } from '@/lib/appBasePath';
import ImpersonationMenu from '@/app/components/ImpersonationMenu';
import { resolveBetaNavPermission } from '@/lib/betaNavAccess';
import { SessionContext } from '@/app/components/Session';
import { useVoiceSubscription } from '@/hooks/useVoiceSubscription';
import { useQueueStatus } from '@/hooks/useOnboardingDemoData';
import { getVoiceTrialSeverity, isVoiceTrialExhausted, type VoiceVertical, type VoiceOutcomeBand } from '@/types/voice-subscription';
import { VoiceTrialPanel } from './components/voice-trial-panel';
import { VoiceOnboardingGate } from './components/voice-onboarding-gate';
import { ToastContext } from '@/app/components/Toast';

// documentationConfig is ~200KB of page copy; load it after hydration instead of shipping it in the root bundle.
const DocumentationButton = dynamic(() => import('@/components/Documentation/DocumentationButton').then((m) => m.DocumentationButton), { ssr: false });

// Closed or inactive on most page loads: load their code on first need instead of in the root bundle.
// (Each is mounted on first need and then left mounted — see the *Mounted refs below — so state and exit animations survive.)
const FreeSessionsUsedModal = dynamic(() => import('./components/free-sessions-used-modal').then((m) => m.FreeSessionsUsedModal), { ssr: false });
const PlatformOnboardingModal = dynamic(() => import('./components/platform-onboarding').then((m) => m.PlatformOnboardingModal), { ssr: false });
const BlynPreviewDrawer = dynamic(() => import('./components/blyn-preview-drawer').then((m) => m.BlynPreviewDrawer), { ssr: false });

const ACTIVATE_VERTICAL_TO_API: Record<string, VoiceVertical | null> = {
  home: 'home_services',
  insurance: 'insurance',
  medicare: 'medicare',
  healthcare: 'healthcare',
  realestate: 'real_estate',
  financial: 'financial',
  other: 'other',
};

// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
  data?: {
    nav?: NavSectionProps['data'];
  };
};

export function DashboardLayout({ sx, children, header, data }: DashboardLayoutProps) {
  const theme = useTheme();
  const router = useRouter();

  const mobileNavOpen = useBoolean();
  const blynDrawer = useBoolean();

  const layoutQuery: Breakpoint = 'lg';

  const isDesktop = useMediaQuery(theme.breakpoints.up(layoutQuery));

  const settings = useSettingsContext();

  const navColorVars = useNavColorVars(theme, settings);
  const navHeaderBg = getNavBackgroundColor(theme, settings);

  const path = usePathname();

  const showSidebar = useMemo(() => !shouldHideAppSidebar(path), [path]);
  const isAdminFullscreen = isAdminPanelFullscreenPath(path);

  // ── Voice trial sidebar panel — surfaces the same free-sessions-remaining
  // ledger shown on the campaign overview tab, but persistently across every
  // voice-module page (not just that one tab of one campaign). ──
  // Voice module routes — includes sibling pages under the Voice nav that do
  // not contain "voice" in the path (Conversations, Knowledge Base).
  const isVoiceRoute = useMemo(() => {
    if (!path) return false;
    if (path.includes('voice')) return true;
    if (path.includes('/conversations')) return true;
    if (path.includes('/knowledge-base')) return true;
    return false;
  }, [path]);
  const { user: sessionUser } = useContext(SessionContext);
  const voiceTenantId = useMemo(() => {
    const raw = sessionUser?.userID;
    if (raw == null) return null;
    const parsed = parseInt(String(raw), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [sessionUser?.userID]);
  const { subscription: voiceSubscription, isLoading: voiceSubLoading, startTrial, setVertical, isMutating: voiceMutating } =
    useVoiceSubscription(isVoiceRoute ? voiceTenantId : null);
  const toast = useContext(ToastContext);
  const rbac = useRBACOptional();
  const voiceTrialLimit = voiceSubscription?.trial_sessions_limit ?? 0;
  const voiceTrialUsed = voiceSubscription?.trial_sessions_used ?? 0;
  const voiceTrialRemaining = voiceSubscription?.trial_sessions_remaining ?? 0;
  // Keep surfacing the free-sessions ledger as long as sessions remain, even
  // after the account converts to a paid plan (trial_active flips to false
  // on upgrade, but unused sessions are still meaningful to show).
  const showVoiceTrialPanel = isVoiceRoute && voiceTrialLimit > 0 && voiceTrialRemaining > 0;

  // First-time users with no subscription — block all voice pages with Activate onboarding.
  // Guard on voiceTenantId being resolved too: while session hydration is still in flight
  // (sessionUser.userID not loaded yet), voiceTenantId is null and useVoiceSubscription(null)
  // immediately reports isLoading=false/subscription=null (it has no tenant to fetch for) —
  // without this check that reads as "confirmed no subscription" and flashes the onboarding
  // gate for the ~2s it takes the session to hydrate, before flipping back once the real
  // subscription loads.
  const showVoiceOnboarding = isVoiceRoute && voiceTenantId != null && !voiceSubLoading && !voiceSubscription;

  // Free-sessions-used modal across all voice pages (not only campaign details).
  // Skip destination pages for the modal CTAs so users can pick a plan / see recap.
  const onVoicePlanOrBillingPage =
    path?.includes('/account/voice-subscription') || path?.includes('/account/voice-billing');
  const showFreeSessionsUsedModal =
    isVoiceRoute &&
    !showVoiceOnboarding &&
    isVoiceTrialExhausted(voiceSubscription) &&
    !onVoicePlanOrBillingPage;
  const { data: voiceQueueStatus } = useQueueStatus(showFreeSessionsUsedModal ? voiceTenantId : null);
  const platformOnboardingEnabled =
    sessionUser?.userID != null &&
    !rbac?.isLoading &&
    !isAdminFullscreen &&
    !showVoiceOnboarding &&
    !showFreeSessionsUsedModal;
  const freeSessionsModalMountedRef = useRef(false);
  const platformOnboardingMountedRef = useRef(false);
  const blynDrawerMountedRef = useRef(false);
  if (showFreeSessionsUsedModal) freeSessionsModalMountedRef.current = true;
  if (platformOnboardingEnabled) platformOnboardingMountedRef.current = true;
  if (blynDrawer.value) blynDrawerMountedRef.current = true;
  const freeSessionsWaiting = voiceQueueStatus?.totalWaiting ?? voiceTrialUsed;
  const freeSessionsBooked = voiceQueueStatus?.recentBookings ?? 0;
  const isFullBleedContent =
    path?.includes('/ai-agent') ||
    path?.includes('/conversations') ||
    path?.includes('/email/inbox') ||
    path?.includes('/creatives/build') ||
    isAdminFullscreen;

  const { customTitle, customBreadcrumbs, customBackAction, customHeaderActions, customLeftActions, hideTitle, setCustomTitle, setCustomBreadcrumbs } = useHeaderContext();

  useEffect(() => {
    if (!showVoiceOnboarding) return;
    setCustomTitle('Choose your vertical');
    setCustomBreadcrumbs([
      { name: 'Onboarding', href: '/account/voice-subscription' },
      { name: 'Choose your vertical' },
    ]);
    return () => {
      setCustomTitle(null);
      setCustomBreadcrumbs(null);
    };
  }, [showVoiceOnboarding, setCustomTitle, setCustomBreadcrumbs]);

  const handleVoiceOnboardingActivate = useCallback(
    async (verticalKey: string, bandKey?: string) => {
      const ownerUserId = rbac?.userContext?.owner_user_id ?? null;
      const canManage = Boolean(voiceTenantId && (!ownerUserId || voiceTenantId === ownerUserId));
      if (!canManage) {
        toast?.error?.('Only the account owner can start a voice trial.');
        return;
      }
      try {
        await startTrial();
        toast?.success?.('Voice trial started! You have 50 free sessions to explore.');
        const apiVertical = ACTIVATE_VERTICAL_TO_API[verticalKey];
        if (apiVertical) {
          try {
            const outcomeBand = apiVertical === 'other' ? (bandKey as VoiceOutcomeBand) : undefined;
            await setVertical(apiVertical, outcomeBand);
          } catch {
            // Trial started — vertical can be set later
          }
        }
      } catch (err: any) {
        toast?.error?.(err?.message ?? 'Failed to start trial');
      }
    },
    [rbac?.userContext?.owner_user_id, voiceTenantId, startTrial, setVertical, toast]
  );

  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('dashboard-sidebar-width');
    if (saved) {
      setSidebarWidth(Number(saved));
    } else if (typeof window !== 'undefined' && window.innerWidth >= 1536) {
      setSidebarWidth(260);
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newWidth = Math.min(Math.max(e.clientX, 160), 480);
    setSidebarWidth(newWidth);
    localStorage.setItem('dashboard-sidebar-width', newWidth.toString());
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [handleMouseMove, handleMouseUp]
  );

  const navDataFiltered = useMemo(() => {
    const hasPermission = (perm: string) => {
      // No RBAC context: hide gated items instead of showing them (fail closed).
      if (!rbac) return false;
      // Beta grants (Command Center, Blyn) need an exact grant — owners don't get them via hasPermission.
      return resolveBetaNavPermission(rbac, perm) ?? rbac.hasPermission(perm);
    };
    return filterNavByPermission(navData || [], hasPermission);
  }, [rbac]);

  // The "Agent" nav item opens the Blyn preview drawer instead of navigating
  // straight to the full /ai-agent app — the drawer's own "Open Blyn" action
  // handles that navigation.
  const dataNav = useMemo(() => {
    const base = data?.nav ?? navDataFiltered;
    return (base || []).map((group: any) => ({
      ...group,
      items: (group.items || []).map((item: any) =>
        item.path === '/ai-agent'
          ? {
              ...item,
              onClick: (event: React.MouseEvent) => {
                event.preventDefault();
                blynDrawer.onTrue();
              },
            }
          : item
      ),
    }));
  }, [data?.nav, navDataFiltered, blynDrawer.onTrue]);

  // Also surface the free-voice-sessions panel inside the mini-nav's "Voice"
  // flyout popover, since that popover doesn't go through sidebarSection's
  // bottomArea slot (which only renders in the full, non-mini sidebar).
  const dataNavWithVoiceFooter = useMemo(() => {
    if (!showVoiceTrialPanel) return dataNav;
    const footer = (
      <VoiceTrialPanel
        used={voiceTrialUsed}
        remaining={voiceTrialRemaining}
        limit={voiceTrialLimit}
        severity={getVoiceTrialSeverity(voiceTrialRemaining, voiceTrialLimit)}
        href="/account/voice-subscription"
        sx={{ mx: 0.5, my: 0.5 }}
      />
    );
    return (dataNav || []).map((group: any) => ({
      ...group,
      items: (group.items || []).map((item: any) =>
        item.title === 'Voice' ? { ...item, footer } : item
      ),
    }));
  }, [dataNav, showVoiceTrialPanel, voiceTrialUsed, voiceTrialRemaining, voiceTrialLimit]);

  const isNavMini = settings.navLayout === 'mini';
  const isNavHorizontal = settings.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.navLayout === 'vertical';
  const appUrl = process.env.NEXT_PUBLIC_SELF_URL;

  const prevPathRef = useRef<string | null>(null);
  useEffect(() => {
    const isFullscreenAgent = path?.includes('/ai-agent') || isAdminPanelFullscreenPath(path);
    const wasFullscreenAgent =
      prevPathRef.current?.includes('/ai-agent') || isAdminPanelFullscreenPath(prevPathRef.current);
    if (isFullscreenAgent && !wasFullscreenAgent && settings.navLayout !== 'mini') {
      settings.onUpdateField('navLayout', 'mini');
    }
    prevPathRef.current = path;
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const flatNav = useMemo(() => {
    return (dataNav || []).flatMap((g: any) => flattenNavItems(g?.items || []));
  }, [dataNav]);
  // Blyn deep links (`/ai-agent/projects`) are not host pages. Treat them as
  // `/ai-agent` so the header title stays "Agent" and the breadcrumb Back
  // button does not appear next to the existing "Back to App" control.
  const headerPath = path?.startsWith('/ai-agent') ? '/ai-agent' : path;

  const currentTitle = useMemo(() => {
    try {
      // Check for custom text first
      if (customRouteTexts[headerPath]) return customRouteTexts[headerPath];

      const match = flatNav.find((i: any) => i?.path === headerPath);
      if (match?.title) return match.title;
      const last = headerPath?.split('/')?.filter(Boolean)?.slice(-1)[0] || '';
      const fallback = last ? last.replace(/[-_]/g, ' ') : 'Dashboard';
      return fallback.replace(/\b\w/g, (c) => c.toUpperCase());
    } catch {
      return 'Dashboard';
    }
  }, [flatNav, headerPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const breadcrumbLinks = useMemo(() => {
    if (!headerPath) return [{ name: 'Dashboard', href: '/dashboard' }];
    const items: any[] = flatNav || [];
    const segments = headerPath.split('/').filter(Boolean);
    const links: Array<{ name: string; href?: string }> = [];

    // Add each ancestor segment that has a matching nav item
    for (let i = 0; i < segments.length - 1; i++) {
      const segPath = '/' + segments.slice(0, i + 1).join('/');
      const navItem = items.find((item: any) => item?.path === segPath);
      if (navItem) {
        links.push({ name: customRouteTexts[segPath] || navItem.title, href: segPath });
      }
    }

    // Add current page
    const exact = items.find((i: any) => i?.path === headerPath);
    const lastSeg = segments[segments.length - 1] || 'Dashboard';
    const currentName = customRouteTexts[headerPath] || exact?.title ||
      lastSeg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    links.push({ name: currentName, href: undefined });

    return links;
  }, [flatNav, headerPath]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          disableElevation={isNavVertical}
          slotProps={{
            toolbar: {
              sx: {
                // Force pure white background from navHeaderBg (resolves to #FFFFFF in light mode)
                bgcolor: navHeaderBg,
                backgroundColor: navHeaderBg,
                backgroundImage: 'none',
                ...(isNavHorizontal && {
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'var(--layout-nav-text-secondary-color)',
                  },
                  [theme.breakpoints.up(layoutQuery)]: {
                    height: 'var(--layout-nav-horizontal-height)',
                  },
                }),
              },
            },
            container: {
              maxWidth: false,
              sx: {
                ...(isNavVertical && showSidebar && { px: { [layoutQuery]: 3 } }),
                ...((path?.includes('/ai-agent') || path?.includes('/creatives/build') || isAdminFullscreen) && {
                  pl: '0px !important',
                  pr: { xs: 2, md: 3 },
                }),
              },
            },
          }}
          sx={{
            bgcolor: navHeaderBg,
            backgroundColor: navHeaderBg,
            backgroundImage: 'none',
            boxShadow: 'none',
            ...header?.sx,
          }}
          slots={{
            topArea: (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            bottomArea: isNavHorizontal ? (
              <NavHorizontal
                data={dataNav}
                layoutQuery={layoutQuery}
                cssVars={navColorVars.section}
              />
            ) : null,
            leftArea: (
              <>
                {/* -- Logo -- */}
                {(isNavHorizontal || path?.includes('/ai-agent') || path?.includes('/creatives/build') || isAdminFullscreen) && (
                  <Box
                    sx={{
                      width: isAdminFullscreen ? 'var(--admin-nav-w, 260px)' : 'var(--blyn-nav-w, 200px)',
                      display: 'flex',
                      alignItems: 'center',
                      pl: 1, // Logo starts exactly at X = 8px (matching sidebar padding)
                      height: 'var(--layout-header-desktop-height, 64px)',
                      borderRight: '1px solid var(--line, rgba(0, 0, 0, 0.12))',
                      flexShrink: 0,
                      mr: 1, // Reduced gap to shift the back button closer to the divider
                      containerType: 'inline-size',
                      overflow: 'hidden',
                    }}
                  >
                    <NextLink
                      href={selfUrlToHomeHref(appUrl)}
                      style={{ display: 'inline-flex', width: '100%', alignItems: 'center' }}
                    >
                      <Box sx={{
                        display: 'inline-flex',
                        '@container (max-width: 100px)': { display: 'none' }
                      }}>
                        <Image
                          alt="boostt.ai logo"
                          src={theme.palette.mode === 'dark' ? "/v2/images/logo-on-dark.png" : "/v2/images/boosttai.png"}
                          width={140}
                          height={35}
                          style={{ objectFit: 'contain' }}
                        />
                      </Box>
                      <Box sx={{
                        display: 'none',
                        justifyContent: 'center',
                        width: '100%',
                        pr: 1, // offset pl:1 from parent for true center
                        '@container (max-width: 100px)': { display: 'inline-flex' }
                      }}>
                        <Image
                          alt="boostt.ai logo"
                          src={theme.palette.mode === 'dark' ? "/v2/images/favicon-light.png" : "/v2/images/favicon-dark.png"}
                          width={28}
                          height={28}
                          style={{ objectFit: 'contain' }}
                        />
                      </Box>
                    </NextLink>
                  </Box>
                )}
                {/* -- Custom left actions (from pages) -- */}
                {customLeftActions}
                {/* -- Nav mobile -- */}
                <MenuButton
                  onClick={mobileNavOpen.onTrue}
                  sx={{
                    mr: 1,
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={dataNav}
                  open={mobileNavOpen.value}
                  onClose={mobileNavOpen.onFalse}
                  cssVars={navColorVars.section}
                />
                {/* -- Current page title (top-left) -- */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                {!hideTitle && (
                  <>
                  {(breadcrumbLinks.length > 1 || customBackAction) && (
                    <Tooltip title="Back" placement="bottom">
                      <IconButton
                        onClick={() => {
                          if (customBackAction) {
                            customBackAction();
                            return;
                          }
                          const from = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('from') : null;
                          if (from) {
                            let target = decodeURIComponent(from);
                            router.push(pathForNextNavigation(target));
                          } else if (breadcrumbLinks.length > 1) {
                            const parentHref = breadcrumbLinks[breadcrumbLinks.length - 2].href;
                            if (parentHref) {
                              router.push(pathForNextNavigation(parentHref));
                            } else {
                              router.back();
                            }
                          } else {
                            router.back();
                          }
                        }}
                        size="medium"
                        sx={{
                          p: 0.75,
                          mr: 1.5,
                          color: 'primary.main',
                          display: { xs: 'none', md: 'inline-flex' },
                          '&:hover': {
                            bgcolor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Iconify icon="icon-park-outline:back" width={22} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Breadcrumbs
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      '& .MuiBreadcrumbs-ol': {
                        alignItems: 'center',
                        lineHeight: 1,
                        flexWrap: 'nowrap',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      },
                      '& .MuiBreadcrumbs-li': {
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      },
                      '& .MuiBreadcrumbs-li:last-of-type': {
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        flexShrink: 1,
                        display: 'block',
                      },
                      '& .MuiBreadcrumbs-separator': {
                        display: 'flex',
                        alignItems: 'center',
                        color: isNavHorizontal ? 'var(--layout-nav-text-disabled-color)' : 'text.disabled',
                        mx: 1,
                        flexShrink: 0,
                      },
                    }}
                  >
                    {(() => {
                      // If customBreadcrumbs exists, use them instead of auto-generated breadcrumbs
                      const linksToRender =
                        customBreadcrumbs && customBreadcrumbs.length > 0
                          ? customBreadcrumbs
                          : customTitle
                            ? [...breadcrumbLinks.slice(0, -1), { name: customTitle, href: undefined }]
                            : breadcrumbLinks;

                      return linksToRender.map((b, idx) => {
                        const isLast = idx === linksToRender.length - 1;
                        const isLinkable = !isLast && !!b.href && b.href !== '#';

                        return !isLinkable ? (
                          <Typography
                            variant="subtitle1"
                            key={`${b.name}-${idx}`}
                            title={isLast ? b.name : undefined}
                            sx={{
                              fontWeight: isLast ? 700 : 500,
                              color: isLast
                                ? (isNavHorizontal ? 'var(--layout-nav-text-primary-color)' : 'text.primary')
                                : (isNavHorizontal ? 'var(--layout-nav-text-disabled-color)' : 'text.disabled'),
                              // Only the last segment (the page/campaign title) is ever
                              // long enough to overflow — it's the one that shrinks and
                              // ellipsizes so the row stays on one line; earlier segments
                              // (e.g. "Direct Mail") stay fixed at their natural width.
                              ...(isLast
                                ? {
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    flexShrink: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }
                                : { flexShrink: 0, whiteSpace: 'nowrap' }),
                            }}
                          >
                            {b.name}
                          </Typography>
                        ) : (
                          <Link
                            variant="subtitle1"
                            key={`${b.name}-${idx}`}
                            component={NextLink}
                            href={b.href!}
                            underline="hover"
                            sx={{
                              fontWeight: 500,
                              color: isNavHorizontal ? 'var(--layout-nav-text-primary-color)' : 'text.primary',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {b.name}
                          </Link>
                        );
                      });
                    })()}
                  </Breadcrumbs>
                  </>
                )}
                </Box>
              </>
            ),
            rightArea: (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.75 }}>
                {/* -- Searchbar -- */}
                <Searchbar data={dataNav} />
                {/* -- Custom Header Actions (from pages) -- */}
                {customHeaderActions}
                {/* -- Release Notes button -- */}
                {/* <ReleaseNotesButton /> */}
                {/* -- Help / Documentation button -- */}
                <DocumentationButton />

                {/* -- Notifications popover -- */}
                <NotificationsDrawer />
                {/* -- Sub-account selector (before settings) -- */}
                <SubAccountSelector />
                {/* -- Settings button -- */}
                <SettingsButton />
                {/* -- Account drawer -- */}
                <AccountDrawer data={[]} />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        !showSidebar ? null : (
          <>
            <NavVertical
              data={dataNavWithVoiceFooter}
              isNavMini={isNavMini}
              layoutQuery={layoutQuery}
              cssVars={navColorVars.section}
              enabledRootRedirect
              slots={{
                bottomArea: !isNavMini && showVoiceTrialPanel ? (
                  <VoiceTrialPanel
                    used={voiceTrialUsed}
                    remaining={voiceTrialRemaining}
                    limit={voiceTrialLimit}
                    severity={getVoiceTrialSeverity(voiceTrialRemaining, voiceTrialLimit)}
                    href="/account/voice-subscription"
                  />
                ) : undefined,
              }}
              onToggleNav={() =>
                settings.onUpdateField(
                  'navLayout',
                  settings.navLayout === 'vertical' ? 'mini' : 'vertical'
                )
              }
            />
            {isDesktop && !isNavMini && !isNavHorizontal && (
              <>
                {/* Split above/below the fixed-position nav collapse button (top: 20,
                    ~28px tall) so its hover/click zone doesn't overlap this drag handle —
                    otherwise the resize highlight flashes right where users aim to collapse
                    the sidebar, making it look like clicking there will resize it. */}
                <Box
                  onMouseDown={handleMouseDown}
                  sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    top: 0,
                    height: 8,
                    left: `calc(var(--layout-nav-vertical-width) - 4px)`,
                    width: '8px',
                    cursor: 'col-resize',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: '50%',
                      width: '2px',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    '&:hover::after': {
                      opacity: 0.5,
                    },
                    ...(isResizing && {
                      '&::after': {
                        opacity: 0.5,
                      },
                    }),
                  }}
                />
                <Box
                  onMouseDown={handleMouseDown}
                  sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    top: 56,
                    bottom: 0,
                    left: `calc(var(--layout-nav-vertical-width) - 4px)`,
                    width: '8px',
                    cursor: 'col-resize',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: '50%',
                      width: '2px',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    '&:hover::after': {
                      opacity: 0.5,
                    },
                    ...(isResizing && {
                      '&::after': {
                        opacity: 0.5,
                      },
                    }),
                  }}
                />
              </>
            )}
          </>
        )
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        ...navColorVars.layout,
        '--layout-transition-easing': 'linear',
        '--layout-transition-duration': isResizing ? '0ms' : '150ms',
        '--layout-nav-mini-width': '53px',
        '--layout-nav-vertical-width': `${sidebarWidth}px`,
        '--layout-nav-horizontal-height': '64px',
        '--layout-dashboard-content-pt': isFullBleedContent ? 0 : theme.spacing(1),
        '--layout-dashboard-content-pb': isFullBleedContent ? 0 : theme.spacing(8),
        '--layout-dashboard-content-px': isFullBleedContent ? 0 : theme.spacing(2),
        ...(isAdminFullscreen && { '--admin-nav-w': '260px' }),
      }}
      sx={{
        // Responsive sidebar widths
        '--layout-nav-mini-width': {
          xs: '48px',
          sm: '50px',
          md: '53px',
        },
        '--layout-nav-vertical-width': {
          xs: '160px',
          sm: '168px',
          md: `${sidebarWidth}px`,
        },
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            transition: isResizing
              ? 'none'
              : theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <Main isNavHorizontal={isNavHorizontal}>
        <ImpersonationMenu />
        <VoiceOnboardingGate
          active={showVoiceOnboarding}
          activating={voiceMutating}
          onActivate={handleVoiceOnboardingActivate}
        >
          {children}
        </VoiceOnboardingGate>
        {freeSessionsModalMountedRef.current && (
          <FreeSessionsUsedModal
            open={showFreeSessionsUsedModal}
            bookedCount={freeSessionsBooked}
            waitingCount={freeSessionsWaiting}
            contentInset={
              isNavHorizontal || !showSidebar
                ? '0px'
                : isNavMini
                  ? 'var(--layout-nav-mini-width)'
                  : 'var(--layout-nav-vertical-width)'
            }
            onChoosePlan={() => router.push('/account/voice-subscription')}
            onSeeResults={() => router.push('/account/voice-billing')}
          />
        )}
        {/* Post-login platform walkthrough — waits for the session user and RBAC so the
            modules step reflects the user's permissions, and yields to the voice gates. */}
        {platformOnboardingMountedRef.current && (
          <PlatformOnboardingModal enabled={platformOnboardingEnabled} nav={navDataFiltered} />
        )}
        {blynDrawerMountedRef.current && (
          <BlynPreviewDrawer
            open={blynDrawer.value}
            onClose={blynDrawer.onFalse}
            onOpenBlyn={() => {
              blynDrawer.onFalse();
              router.push('/ai-agent');
            }}
            pageTitle={currentTitle}
            dateLabel="Today"
            headline={`Ask Blyn anything about ${currentTitle}.`}
            bodyParagraphs={[
              "Blyn hasn't generated a brief for this view yet. Ask a question below, or open the full agent for deeper analysis.",
            ]}
            suggestions={[
              { id: 'changed', tag: 'CHANGES', question: `What changed on ${currentTitle} since yesterday?` },
              { id: 'focus', tag: 'PRIORITY', question: `Where should I focus on ${currentTitle} right now?` },
              { id: 'summary', tag: 'SUMMARY', question: `Summarize ${currentTitle} for me.` },
            ]}
            onAsk={() => {
              blynDrawer.onFalse();
              router.push('/ai-agent');
            }}
          />
        )}
      </Main>
    </LayoutSection>
  );
}
